// ---------------------------------------------------------------------------
// Survive on BCH — Simulated Game Data
//
// ALL VALUES ARE SIMULATED. These prices, fees, discounts and exchange rates
// are invented for gameplay and do not represent real markets or real money.
// ---------------------------------------------------------------------------

import type { DayMarket, GameMode, Scenario } from "./types";

export const TOTAL_DAYS = 7;

/** Simulated starting balances (USD terms). */
export const STARTING_FIAT_USD = 300;
export const STARTING_CRYPTO_USD = 700;

/**
 * Simulated BCH on-chain fee (USD terms). BCH has 32MB blocks that run far
 * below capacity, so there's no fee market — the fee is a fraction of a cent
 * and stays FLAT no matter how busy the network gets. This is the whole point.
 */
export const BCH_FEE_USD = 0.004;

/** Simulated BTC Lightning fee — tiny, like BCH on-chain. */
export const BTC_LIGHTNING_FEE_USD = 0.003;

/**
 * Backwards-compatible headline fee figures used on the start screen.
 * BTC value is the calm-day baseline; real per-day BTC fees live in MARKETS
 * and can spike well above this on congested days.
 */
export const CRYPTO_FEE_USD = {
  BCH: BCH_FEE_USD,
  BTC: 0.5, // BTC on-chain calm-day baseline
} as const;

/**
 * The 7-day SIMULATED price trajectory, expressed as multipliers relative to
 * day 1 (day 1 = 1.0). This encodes the game's designed volatility so every
 * playthrough sees the same comparable week. The ABSOLUTE prices are anchored
 * to a real, live-fetched day-1 price (see buildMarkets / useLivePrices), so
 * the numbers on screen are real while the week's shape stays consistent.
 *
 * BTC on-chain fees VARY BY DAY. On calm days they're ~$0.30–$0.80, but when
 * the mempool gets congested (a price rally, an ordinals/NFT rush, an exchange
 * batch storm) they spike to many dollars. BCH's fee never moves. Fees are
 * simulated but the behavior mirrors real fee-market dynamics.
 */
interface MarketTemplate {
  day: number;
  drift: Record<GameMode, number>;
  headline: string;
  btcOnchainFeeUsd: number;
  btcCongested: boolean;
  networkNote?: string;
}

const MARKET_TEMPLATE: MarketTemplate[] = [
  {
    day: 1,
    drift: { BCH: 1.0, BTC: 1.0 },
    headline: "Markets calm. A fresh start.",
    btcOnchainFeeUsd: 0.45,
    btcCongested: false,
    networkNote: "Both networks quiet. BTC fees low today.",
  },
  {
    day: 2,
    drift: { BCH: 1.016, BTC: 0.99 },
    headline: "BCH ticks up, BTC dips a little.",
    btcOnchainFeeUsd: 0.7,
    btcCongested: false,
    networkNote: "BTC mempool filling up a bit.",
  },
  {
    day: 3,
    drift: { BCH: 0.988, BTC: 1.019 },
    headline: "BTC pumps on ETF inflows — mempool floods.",
    btcOnchainFeeUsd: 18,
    btcCongested: true,
    networkNote: "⚠️ BTC congested! Next-block fees have spiked to ~$18. BCH unaffected.",
  },
  {
    day: 4,
    drift: { BCH: 1.032, BTC: 0.985 },
    headline: "Choppy day. BTC backlog still clearing.",
    btcOnchainFeeUsd: 3.5,
    btcCongested: false,
    networkNote: "BTC fees elevated as the backlog drains.",
  },
  {
    day: 5,
    drift: { BCH: 1.008, BTC: 1.004 },
    headline: "Sideways markets. Networks normalize.",
    btcOnchainFeeUsd: 0.6,
    btcCongested: false,
    networkNote: "BTC fees back to normal.",
  },
  {
    day: 6,
    drift: { BCH: 1.045, BTC: 1.032 },
    headline: "Green candles — another BTC fee bump.",
    btcOnchainFeeUsd: 6,
    btcCongested: true,
    networkNote: "⚠️ BTC busy again; on-chain fees around $6. BCH still a fraction of a cent.",
  },
  {
    day: 7,
    drift: { BCH: 1.02, BTC: 1.013 },
    headline: "Final stretch. Make it count.",
    btcOnchainFeeUsd: 0.8,
    btcCongested: false,
    networkNote: "Calm finish for both networks.",
  },
];

/** Default day-1 anchor prices (used until live prices load / on failure). */
export const DEFAULT_ANCHOR: Record<GameMode, number> = {
  BCH: 250,
  BTC: 79000,
};

/**
 * Build the 7-day market from a day-1 anchor price (real, when available).
 * The drift pattern is applied on top so the week's shape stays consistent.
 */
export function buildMarkets(anchor: Record<GameMode, number>): DayMarket[] {
  return MARKET_TEMPLATE.map((t) => ({
    day: t.day,
    priceUsd: {
      BCH: Math.round(anchor.BCH * t.drift.BCH * 100) / 100,
      BTC: Math.round(anchor.BTC * t.drift.BTC),
    },
    headline: t.headline,
    btcOnchainFeeUsd: t.btcOnchainFeeUsd,
    btcCongested: t.btcCongested,
    networkNote: t.networkNote,
  }));
}

/**
 * Default markets using the fallback anchor. This is the module-level constant
 * used by pure engine functions; the live game passes a live-anchored version
 * through explicitly where prices matter.
 */
export const MARKETS: DayMarket[] = buildMarkets(DEFAULT_ANCHOR);

/**
 * Seven days of scenarios. Both modes use the SAME scenarios so results are
 * comparable. MOST merchants accept both coins and earn the same crypto
 * discount, so the primary differentiator is the per-transaction NETWORK FEE
 * (and day-to-day price volatility). A few merchants are single-coin to reflect
 * reality — some are BCH-only (small vendors who love near-zero fees) and some
 * are BTC/Lightning-only (apps that integrated BTC first). When your coin isn't
 * accepted, you fall back to fiat at full price with a small wellbeing hit.
 *
 * The week also mixes in EARN scenarios (flow: "earn") — getting paid. Here the
 * story flips: a crypto payout arrives near-instantly and the payer's network
 * fee is netted out of what you receive (a fraction of a cent in BCH, a real
 * bite on a congested BTC day), while a fiat payout skims a percentage service
 * cut and settles a day or two later.
 */
export const SCENARIOS: Scenario[] = [
  // ---- DAY 1 -------------------------------------------------------------
  {
    id: "d1-breakfast",
    day: 1,
    icon: "🥐",
    category: "Food",
    title: "Morning Coffee & Pastry",
    description:
      "The corner café has a big 'Crypto Accepted' sign. You need breakfast to start the day.",
    options: [
      {
        id: "crypto",
        label: "Pay with coin",
        method: "crypto",
        priceUsd: 6,
        cryptoDiscount: 0.1,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "Quick scan, 10% off. The barista even asks about your wallet.",
        wellbeing: 8,
      },
      {
        id: "fiat",
        label: "Pay with fiat",
        method: "fiat",
        priceUsd: 6,
        outcome: "Cash works fine, no discount though.",
        wellbeing: 6,
      },
      {
        id: "skip",
        label: "Skip breakfast",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You save money but your stomach growls all morning.",
        wellbeing: -8,
      },
    ],
  },
  {
    id: "d1-transit",
    day: 1,
    icon: "🚌",
    category: "Transport",
    title: "Getting to Work",
    description: "The bus app now takes crypto. It's a 40-minute walk otherwise.",
    options: [
      {
        id: "crypto",
        label: "Pay fare with coin",
        method: "crypto",
        priceUsd: 3,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "The transit app processes your payment instantly.",
        wellbeing: 6,
      },
      {
        id: "fiat",
        label: "Buy ticket with fiat",
        method: "fiat",
        priceUsd: 3.5,
        outcome: "Paper ticket, slightly pricier at the kiosk.",
        wellbeing: 5,
      },
      {
        id: "skip",
        label: "Walk it",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "Free, but you arrive tired and a little late.",
        wellbeing: -5,
      },
    ],
  },

  {
    id: "d1-gig",
    day: 1,
    icon: "💼",
    category: "Get Paid",
    flow: "earn",
    title: "Freelance Gig Payout",
    description:
      "You finished a $180 design job. The client will pay you however you like — direct in crypto, or through a freelance platform that handles fiat.",
    options: [
      {
        id: "crypto",
        label: "Get paid in coin",
        method: "crypto",
        priceUsd: 180,
        acceptsBch: true,
        acceptsBtc: true,
        outcome:
          "Paid peer-to-peer, straight to your wallet. The only thing taken out is the network fee — a fraction of a cent in BCH, but a real bite on a busy BTC day.",
        wellbeing: 12,
      },
      {
        id: "fiat",
        label: "Get paid via platform",
        method: "fiat",
        priceUsd: 180,
        fiatCut: 0.1,
        settleDays: 2,
        outcome:
          "The freelance platform skims a 10% fee and holds the payout for two days before it lands in your bank.",
        wellbeing: 6,
      },
      {
        id: "skip",
        label: "Invoice later",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You put off invoicing. No money today, and you'll have to chase it up.",
        wellbeing: -8,
      },
    ],
  },

  // ---- DAY 2 -------------------------------------------------------------
  {
    id: "d2-lunch",
    day: 2,
    icon: "🌮",
    category: "Food",
    title: "Lunch at the Taco Stand",
    description:
      "A local favorite. The owner runs a simple BCH point-of-sale and gives a discount for it — no BTC setup here.",
    options: [
      {
        id: "crypto",
        label: "Pay with coin",
        method: "crypto",
        priceUsd: 9,
        cryptoDiscount: 0.15,
        acceptsBch: true,
        acceptsBtc: false,
        outcome: "15% off for paying in BCH! The owner's little POS only takes BCH, though.",
        wellbeing: 10,
      },
      {
        id: "fiat",
        label: "Pay with fiat",
        method: "fiat",
        priceUsd: 9,
        outcome: "Solid lunch, full price.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Eat leftovers",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "Cold leftovers. Not exciting, but free.",
        wellbeing: -3,
      },
    ],
  },
  {
    id: "d2-phone",
    day: 2,
    icon: "📱",
    category: "Phone / Data",
    title: "Top Up Your Data Plan",
    description: "You're almost out of mobile data. A gateway lets you top up with crypto.",
    options: [
      {
        id: "crypto",
        label: "Top up with coin",
        method: "crypto",
        priceUsd: 15,
        cryptoDiscount: 0.05,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "Top-up confirmed. On a small amount like this, the network fee really matters.",
        wellbeing: 7,
      },
      {
        id: "fiat",
        label: "Pay with fiat card",
        method: "fiat",
        priceUsd: 15,
        outcome: "Card payment goes through fine.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Ration your data",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You turn off background apps and hope for wifi.",
        wellbeing: -6,
      },
    ],
  },

  {
    id: "d2-sale",
    day: 2,
    icon: "📦",
    category: "Get Paid",
    flow: "earn",
    title: "Marketplace Sale",
    description:
      "You sold an old camera for $95 on a local marketplace. The buyer is standing right there, wallet open, ready to pay on the spot.",
    options: [
      {
        id: "crypto",
        label: "Take payment in coin",
        method: "crypto",
        priceUsd: 95,
        acceptsBch: true,
        acceptsBtc: true,
        outcome:
          "The buyer scans and pays instantly — no waiting for a bank, no chargeback risk. Whatever the network fee is, that's all it costs you.",
        wellbeing: 10,
      },
      {
        id: "fiat",
        label: "Take payment by app transfer",
        method: "fiat",
        priceUsd: 95,
        fiatCut: 0.03,
        settleDays: 2,
        outcome:
          "The payment app takes a 3% seller fee and the funds are 'pending' for a couple of days.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Hold out for a better offer",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You pass on the sale hoping for more later. The buyer walks away.",
        wellbeing: -5,
      },
    ],
  },

  // ---- DAY 3 -------------------------------------------------------------
  {
    id: "d3-rent",
    day: 3,
    icon: "🏠",
    category: "Big Payment",
    title: "Rent Is Due Today",
    description:
      "Rent — $450 — is due, and it lands on a congested BTC day. Your landlord takes crypto on-chain or a bank transfer.",
    options: [
      {
        id: "crypto",
        label: "Pay rent with coin (on-chain)",
        method: "crypto",
        priceUsd: 450,
        acceptsBch: true,
        acceptsBtc: true,
        outcome:
          "A big on-chain payment. The fee is a flat fraction of a cent in BCH — but on a congested BTC day, the same transfer carries a painful network fee no matter how large the amount.",
        wellbeing: 6,
      },
      {
        id: "fiat",
        label: "Pay rent by bank transfer",
        method: "fiat",
        priceUsd: 450,
        outcome: "Bank transfer clears. No crypto fee, but it takes a couple of days.",
        wellbeing: 6,
      },
      {
        id: "skip",
        label: "Ask for a one-day extension",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "The landlord grumbles but grants a short extension. Stressful.",
        wellbeing: -14,
      },
    ],
  },
  {
    id: "d3-remittance",
    day: 3,
    icon: "💸",
    category: "Send Money",
    title: "Send Money to Family",
    description:
      "Your family needs $40. You can send it cross-border with crypto or use a money transfer service.",
    options: [
      {
        id: "crypto",
        label: "Send with coin",
        method: "crypto",
        priceUsd: 40,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "Sent directly, no middleman. The only cost is the network fee — which is exactly where the two coins differ.",
        wellbeing: 12,
      },
      {
        id: "fiat",
        label: "Use transfer service",
        method: "fiat",
        priceUsd: 46,
        outcome: "The service charges a $6 flat fee and takes two days.",
        wellbeing: 8,
      },
      {
        id: "skip",
        label: "Send it next week",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You delay. Family is disappointed.",
        wellbeing: -12,
      },
    ],
  },
  {
    id: "d3-groceries",
    day: 3,
    icon: "🛒",
    category: "Shopping",
    title: "Weekly Groceries",
    description: "The supermarket added a crypto checkout lane this month.",
    options: [
      {
        id: "crypto",
        label: "Checkout with coin",
        method: "crypto",
        priceUsd: 28,
        cryptoDiscount: 0.08,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "8% loyalty discount for paying in crypto at the self-checkout.",
        wellbeing: 9,
      },
      {
        id: "fiat",
        label: "Checkout with fiat",
        method: "fiat",
        priceUsd: 28,
        outcome: "Normal checkout, full price.",
        wellbeing: 8,
      },
      {
        id: "skip",
        label: "Buy only the basics",
        method: "fiat",
        priceUsd: 12,
        skip: true,
        outcome: "You grab just rice and beans to save money.",
        wellbeing: -4,
      },
    ],
  },

  // ---- DAY 4 -------------------------------------------------------------
  {
    id: "d4-emergency",
    day: 4,
    icon: "🩹",
    category: "Unexpected",
    title: "Surprise: Pharmacy Run",
    description:
      "You wake up with a nasty cold. The 24h pharmacy takes crypto at the counter.",
    options: [
      {
        id: "crypto",
        label: "Pay with coin",
        method: "crypto",
        priceUsd: 18,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "Meds acquired. Tap-to-pay goes through — the fee is the only thing that varies by coin.",
        wellbeing: 10,
      },
      {
        id: "fiat",
        label: "Pay with fiat",
        method: "fiat",
        priceUsd: 18,
        outcome: "Card payment, you get your medicine.",
        wellbeing: 10,
      },
      {
        id: "skip",
        label: "Tough it out",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You skip the meds and feel awful all day.",
        wellbeing: -15,
      },
    ],
  },
  {
    id: "d4-dinner",
    day: 4,
    icon: "🍜",
    category: "Food",
    title: "Comfort Dinner",
    description: "A warm bowl of noodles would help you feel better. The shop is crypto-friendly.",
    options: [
      {
        id: "crypto",
        label: "Pay with coin",
        method: "crypto",
        priceUsd: 11,
        cryptoDiscount: 0.12,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "12% off for paying in crypto. Exactly what you needed.",
        wellbeing: 9,
      },
      {
        id: "fiat",
        label: "Pay with fiat",
        method: "fiat",
        priceUsd: 11,
        outcome: "Full price, still delicious.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Instant noodles at home",
        method: "fiat",
        priceUsd: 1.5,
        skip: true,
        outcome: "Cheap and filling, if a little sad.",
        wellbeing: -2,
      },
    ],
  },

  {
    id: "d4-tips",
    day: 4,
    icon: "🎸",
    category: "Get Paid",
    flow: "earn",
    title: "Tips From Your Street Set",
    description:
      "You busk on the side. Tonight the crowd loved it — about $60 in tips, but it comes as lots of tiny payments. Small amounts are exactly where a per-transaction fee hurts most.",
    options: [
      {
        id: "crypto",
        label: "Collect tips in coin",
        method: "crypto",
        priceUsd: 60,
        acceptsBch: true,
        acceptsBtc: true,
        outcome:
          "Dozens of little tips land in your wallet. In BCH each one costs a fraction of a cent to receive; on BTC's still-busy network, tiny on-chain tips get chewed up by fees.",
        wellbeing: 11,
      },
      {
        id: "fiat",
        label: "Collect tips via tip-jar app",
        method: "fiat",
        priceUsd: 60,
        fiatCut: 0.08,
        settleDays: 2,
        outcome:
          "The tip-jar app bundles everything but skims 8% and pays out to your bank in a couple of days.",
        wellbeing: 6,
      },
      {
        id: "skip",
        label: "Pack up early",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You call it a night before the crowd builds. Quiet, but no tips.",
        wellbeing: -6,
      },
    ],
  },

  // ---- DAY 5 -------------------------------------------------------------
  {
    id: "d5-rideshare",
    day: 5,
    icon: "🚗",
    category: "Transport",
    title: "Rideshare Across Town",
    description:
      "You need to get across town fast. This rideshare app added crypto checkout — but only through a BTC Lightning integration.",
    options: [
      {
        id: "crypto",
        label: "Pay ride with coin",
        method: "crypto",
        priceUsd: 14,
        cryptoDiscount: 0.05,
        acceptsBch: false,
        acceptsBtc: true,
        btcLightning: true,
        outcome: "Paid over BTC Lightning for a tiny fee, and got 5% off. Lightning is fast and cheap for small buys.",
        wellbeing: 7,
      },
      {
        id: "fiat",
        label: "Pay ride with fiat",
        method: "fiat",
        priceUsd: 14,
        outcome: "App charges your card, standard rate.",
        wellbeing: 6,
      },
      {
        id: "skip",
        label: "Take the slow bus",
        method: "crypto",
        priceUsd: 3,
        acceptsBch: true,
        acceptsBtc: true,
        skip: true,
        outcome: "Cheaper, but you lose an hour of your day.",
        wellbeing: -3,
      },
    ],
  },
  {
    id: "d5-shopping",
    day: 5,
    icon: "👕",
    category: "Shopping",
    title: "You Need New Shoes",
    description: "Your old sneakers finally gave out. A local shop runs a crypto promo.",
    options: [
      {
        id: "crypto",
        label: "Buy with coin",
        method: "crypto",
        priceUsd: 35,
        cryptoDiscount: 0.2,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "Huge 20% crypto-day discount for paying with any coin!",
        wellbeing: 11,
      },
      {
        id: "fiat",
        label: "Buy with fiat",
        method: "fiat",
        priceUsd: 35,
        outcome: "Full price, but you get comfy new shoes.",
        wellbeing: 8,
      },
      {
        id: "skip",
        label: "Patch up the old ones",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "Duct tape holds them together for now.",
        wellbeing: -6,
      },
    ],
  },

  {
    id: "d5-remittance-in",
    day: 5,
    icon: "🌍",
    category: "Get Paid",
    flow: "earn",
    title: "Money From Family Abroad",
    description:
      "A relative overseas is sending you $120 to help out. They can send it as crypto directly, or through a traditional remittance service.",
    options: [
      {
        id: "crypto",
        label: "Receive it in coin",
        method: "crypto",
        priceUsd: 120,
        acceptsBch: true,
        acceptsBtc: true,
        outcome:
          "It crosses the border in seconds, no bank in the middle. The whole amount arrives minus only the tiny network fee.",
        wellbeing: 13,
      },
      {
        id: "fiat",
        label: "Receive via money-transfer service",
        method: "fiat",
        priceUsd: 120,
        fiatCut: 0.07,
        settleDays: 2,
        outcome:
          "The remittance service takes a 7% cut of cross-border transfers and you collect it a couple of days later.",
        wellbeing: 6,
      },
      {
        id: "skip",
        label: "Tell them not to worry",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You wave off the help out of pride. Kind, but your wallet feels it.",
        wellbeing: -4,
      },
    ],
  },

  // ---- DAY 6 -------------------------------------------------------------
  {
    id: "d6-laptop",
    day: 6,
    icon: "💻",
    category: "Big Payment",
    title: "Refurbished Laptop Deal",
    description:
      "Your laptop died. A marketplace seller has a refurbished one for $220 and prefers an on-chain crypto payment to avoid chargebacks.",
    options: [
      {
        id: "crypto",
        label: "Buy laptop with coin (on-chain)",
        method: "crypto",
        priceUsd: 220,
        cryptoDiscount: 0.05,
        acceptsBch: true,
        acceptsBtc: true,
        outcome:
          "Seller knocks off 5% for paying in crypto. On-chain settles either way — but BTC's network is busy again today, so that fee stings on top.",
        wellbeing: 9,
      },
      {
        id: "fiat",
        label: "Buy with fiat card",
        method: "fiat",
        priceUsd: 220,
        outcome: "Card payment goes through, full price.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Limp along without one",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You borrow a friend's laptop when you can. Inconvenient all week.",
        wellbeing: -8,
      },
    ],
  },
  {
    id: "d6-bills",
    day: 6,
    icon: "🧾",
    category: "Unexpected",
    title: "Utility Bill Due",
    description:
      "Your electricity bill arrived. The utility takes crypto through a processor that, for now, only settles BTC on-chain.",
    options: [
      {
        id: "crypto",
        label: "Pay bill with coin",
        method: "crypto",
        priceUsd: 22,
        acceptsBch: false,
        acceptsBtc: true,
        outcome: "Paid via the BTC processor. On-chain settlement adds a small network fee to a routine bill.",
        wellbeing: 8,
      },
      {
        id: "fiat",
        label: "Pay bill with fiat",
        method: "fiat",
        priceUsd: 22,
        outcome: "Bank transfer completed on time.",
        wellbeing: 8,
      },
      {
        id: "skip",
        label: "Pay it late",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You risk a late fee and a stressful reminder call.",
        wellbeing: -10,
      },
    ],
  },
  {
    id: "d6-treat",
    day: 6,
    icon: "🍦",
    category: "Food",
    title: "Treat Yourself",
    description:
      "You've been careful all week. A tiny ice cream shop runs a BCH-only happy hour — the owner loves the near-zero fees on small sales.",
    options: [
      {
        id: "crypto",
        label: "Pay with coin",
        method: "crypto",
        priceUsd: 5,
        cryptoDiscount: 0.25,
        acceptsBch: true,
        acceptsBtc: false,
        outcome: "25% off during the BCH happy hour. Sweet deal, literally.",
        wellbeing: 9,
      },
      {
        id: "fiat",
        label: "Pay with fiat",
        method: "fiat",
        priceUsd: 5,
        outcome: "A well-earned treat at full price.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Skip the treat",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "You save the money but feel a bit deprived.",
        wellbeing: -3,
      },
    ],
  },

  // ---- DAY 7 -------------------------------------------------------------
  {
    id: "d7-feast",
    day: 7,
    icon: "🍽️",
    category: "Food",
    title: "Final Day Feast",
    description:
      "You made it to day 7! A restaurant is celebrating with a big crypto-diner discount.",
    options: [
      {
        id: "crypto",
        label: "Celebrate with coin",
        method: "crypto",
        priceUsd: 24,
        cryptoDiscount: 0.18,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "18% off for paying in crypto. A perfect way to finish the week.",
        wellbeing: 13,
      },
      {
        id: "fiat",
        label: "Celebrate with fiat",
        method: "fiat",
        priceUsd: 24,
        outcome: "A lovely meal at full price.",
        wellbeing: 10,
      },
      {
        id: "skip",
        label: "Cook at home",
        method: "fiat",
        priceUsd: 6,
        skip: true,
        outcome: "A simple home-cooked meal to cap off the week.",
        wellbeing: 2,
      },
    ],
  },
  {
    id: "d7-gift",
    day: 7,
    icon: "🎁",
    category: "Send Money",
    title: "Tip a Street Musician",
    description:
      "A busker made your whole week. You can tip instantly with a wallet scan.",
    options: [
      {
        id: "crypto",
        label: "Tip with coin",
        method: "crypto",
        priceUsd: 4,
        acceptsBch: true,
        acceptsBtc: true,
        outcome: "A tiny tip lands on-chain. In BCH the fee is a fraction of a cent; a BTC on-chain fee can eat a noticeable slice of a small tip like this.",
        wellbeing: 8,
      },
      {
        id: "fiat",
        label: "Tip with cash",
        method: "fiat",
        priceUsd: 4,
        outcome: "You drop a few bills in the case. Classic.",
        wellbeing: 7,
      },
      {
        id: "skip",
        label: "Just applaud",
        method: "fiat",
        priceUsd: 0,
        skip: true,
        outcome: "Free, and still appreciated — but you feel a little cheap.",
        wellbeing: -2,
      },
    ],
  },
];

/** Scenarios grouped by day, in play order. */
export function scenariosForDay(day: number): Scenario[] {
  return SCENARIOS.filter((s) => s.day === day);
}
